import { ChevronRight, ClipboardList } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { OrderStatusBadge } from "@/features/orders/components/order-status-badge"
import { kgToMt, formatDate, formatNumber } from "@/lib/format"
import { paths } from "@/routes/paths"
import type { OrderListItem } from "@/types/domain"

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

interface OrdersTableProps {
  orders: OrderListItem[]
  contractLabelsById?: Record<string, string>
}

export function OrdersTable({ orders, contractLabelsById }: OrdersTableProps) {
  const { t, i18n } = useTranslation(["orders", "contracts", "common"])
  const navigate = useNavigate()

  if (orders.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ClipboardList className="size-8" />}
          title={t("table.empty")}
          description={t("subtitle")}
          className="border-0 py-16"
        />
      </Card>
    )
  }

  function contractLabel(order: OrderListItem): {
    label: string
    to: string | null
  } {
    const id = order.exporterOrderId?.id
    if (!id) return { label: "—", to: null }
    const idStr = String(id)
    const label = contractLabelsById?.[idStr] ?? `#${idStr}`
    return { label, to: paths.contractDetail(idStr) }
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className={headCellClass}>{t("table.orderNumber")}</TableHead>
              <TableHead className={headCellClass}>{t("table.cooperative")}</TableHead>
              <TableHead className={headCellClass}>{t("tableExtras.contract")}</TableHead>
              <TableHead className={headCellClass}>{t("table.client")}</TableHead>
              <TableHead className={headCellClass}>{t("table.product")}</TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("table.quantity")}
              </TableHead>
              <TableHead className={`${headCellClass} min-w-[140px]`}>
                {t("table.status")}
              </TableHead>
              <TableHead className={headCellClass}>{t("table.deadline")}</TableHead>
              <TableHead aria-hidden className={`${headCellClass} w-8`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              const contract = contractLabel(order)
              return (
                <TableRow
                  key={String(order.id)}
                  className="hover:bg-muted/60 cursor-pointer"
                  onClick={() => navigate(paths.orderDetail(order.id))}
                >
                  <TableCell className={`${bodyCellClass} font-medium`}>
                    {order.orderNumber ?? `#${order.id}`}
                  </TableCell>
                  <TableCell className={`${bodyCellClass} max-w-[200px] truncate`}>
                    {order.groupId?.name ?? ""}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {contract.to ? (
                      <Link
                        to={contract.to}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {contract.label}
                      </Link>
                    ) : (
                      contract.label
                    )}
                  </TableCell>
                  <TableCell className={`${bodyCellClass} max-w-[200px] truncate`}>
                    {order.customerId?.clientName ?? ""}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {order.productType
                      ? t(`options.productTypes.${order.productType}`, {
                          ns: "contracts",
                          defaultValue: order.productType,
                        })
                      : ""}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {formatNumber(kgToMt(order.agreedQuantity ?? 0))}{" "}
                    {t("units.mt", { ns: "common" })}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <OrderStatusBadge status={order.orderStatus ?? null} />
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {formatDate(order.dateOfExportToPort, i18n.language) || "—"}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ChevronRight className="text-muted-foreground size-4" />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}
